/* =========================================================================
   BranchAutoWithdrawals.tsx — the branch's AUTOMATIC withdraw queue.

   Unlike Manual Withdraw (bank transfer by hand + proof screenshot), these are
   gateway payouts: CCPayment and NOWPayments in one list. Approving sends the
   crypto immediately — there is no proof to upload, because the gateway's own
   webhook supplies the on-chain tx hash.

   Amounts arrive in crypto but agent ranges are configured in ₹, so every row
   shows both: the coin amount the player asked for and the INR equivalent that
   decided which agent it was routed to.
   ========================================================================= */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight, Download, RefreshCw } from 'lucide-react';
import {
  AutoProvider, AutoWithdrawRow, branchAutoWithdrawals, branchApproveAutoWithdraw,
  branchRejectAutoWithdraw, branchTwoFaStatus, downloadBranchReport, getBranchUser,
} from '../../services/branchApi';

const SIZE = 25;

const STATE_COLOR: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  pending: 'warning', processing: 'info', success: 'success', failed: 'error', rejected: 'error',
};
const GATEWAY = { ccpayment: 'CCPayment', nowpayments: 'NOWPayments' };

const coin = (v: number, c: string) => `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 8 })} ${c}`;
const short = (s: string | null, n = 14) =>
  !s ? '-' : s.length <= n ? s : `${s.slice(0, n - 6)}…${s.slice(-5)}`;
const when = (v: string | null) => (v ? new Date(v).toLocaleString() : '-');

export default function BranchAutoWithdrawals() {
  const [rows, setRows] = useState<AutoWithdrawRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [state, setState] = useState('pending');
  const [provider, setProvider] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [action, setAction] = useState<{ row: AutoWithdrawRow; kind: 'approve' | 'reject' } | null>(null);
  const [remark, setRemark] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState('');

  // The COMPANY key (Super Admin's). Read-only — nobody enrols one here.
  const [twoFaEnabled, setTwoFaEnabled] = useState<boolean | null>(null);
  const loadTwoFa = useCallback(async () => {
    try { setTwoFaEnabled(!!(await branchTwoFaStatus()).isEnabled); }
    catch { setTwoFaEnabled(null); }
  }, []);

  // The Super Master and anyone granted the withdraw capability can act — the
  // same rule that governs manual withdrawals.
  const user = getBranchUser();
  const canAct = !!(user?.isManager || user?.canWithdraw);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await branchAutoWithdrawals({ state, provider, search, page, limit: SIZE });
      setRows(res.data || []);
      setTotal(res.meta?.total || 0);
    } catch (e: any) {
      setErr(e?.message || 'Failed to load the queue');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [state, provider, search, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTwoFa(); }, [loadTwoFa]);
  useEffect(() => { setPage(1); }, [state, provider, search]);

  const open = (row: AutoWithdrawRow, kind: 'approve' | 'reject') => {
    setAction({ row, kind }); setRemark(''); setCode(''); setActionErr('');
  };

  const submit = async () => {
    if (!action || !remark.trim()) return;
    if (needsCode && code.trim().length !== 6) return;
    setBusy(true);
    setActionErr('');
    try {
      const fn = action.kind === 'approve' ? branchApproveAutoWithdraw : branchRejectAutoWithdraw;
      await fn(action.row.provider as AutoProvider, action.row.id, remark.trim(), code.trim());
      setAction(null);
      await load();
    } catch (e: any) {
      // A rejected code is consumed either way, so clear it — reusing the same
      // digits would fail again as a replay.
      setCode('');
      setActionErr(e?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  // Which second factor this action needs — kept identical to the admin panel's
  // tab for the same gateway:
  //   approve NOWPayments → NOWPayments' own account code, checked by them
  //   approve/reject CCPayment → the company key, checked by us
  //   reject NOWPayments → none; it never reaches the gateway, so there is no
  //     NOWPayments code to verify, and the money returns to the player
  const isNow = action?.row.provider === 'nowpayments';
  const usesGatewayCode = action?.kind === 'approve' && isNow;
  const needsCode = !(action?.kind === 'reject' && isNow);
  const codeLabel = usesGatewayCode ? 'NOWPayments account 2FA code' : 'Super Admin 2FA code';
  const codeHelp = usesGatewayCode
    ? 'The 6-digit code from the NOWPayments account authenticator — NOWPayments checks it.'
    : 'The 6-digit code from the company authenticator — each code works once.';

  const pages = Math.max(1, Math.ceil(total / SIZE));
  const cols = ['SN#', 'Requested', 'Gateway', 'Client', 'Coin / Chain', 'Address', 'Amount',
    'Tx Hash', 'Remark', 'Action By', 'Status', ...(canAct ? ['Actions'] : [])];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Auto Withdraw{canAct ? '' : ' Report'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {canAct
              ? 'Crypto payouts via CCPayment / NOWPayments — approving sends the money immediately, rejecting refunds the player'
              : 'All automatic withdraw requests for this branch'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField size="small" label="Search" placeholder="user / address / tx" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 180 }} />
          <TextField select size="small" label="Gateway" sx={{ minWidth: 150 }}
            value={provider} onChange={(e) => setProvider(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="ccpayment">CCPayment</MenuItem>
            <MenuItem value="nowpayments">NOWPayments</MenuItem>
          </TextField>
          <TextField select size="small" label="Status" sx={{ minWidth: 150 }}
            value={state} onChange={(e) => setState(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="success">Success</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
          <Tooltip title="Refresh"><IconButton onClick={load}><RefreshCw size={16} /></IconButton></Tooltip>
          <Tooltip title="Download CSV">
            <IconButton onClick={() => downloadBranchReport('auto-withdrawals', { state, provider, search } as any)}>
              <Download size={16} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Nothing to enrol here — this only explains why the buttons below would
          refuse, and who has to fix it. */}
      {canAct && twoFaEnabled === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The company authenticator is not set up yet — a Super Admin must enable 2FA in
          the admin panel before CCPayment payouts or any rejection can be actioned.
          NOWPayments approvals are unaffected: they use the NOWPayments account code.
        </Alert>
      )}

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>{cols.map((h) => (
                <TableCell key={h} sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}</TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={`${r.provider}-${r.id}`} hover>
                  <TableCell>{(page - 1) * SIZE + i + 1}</TableCell>
                  <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{when(r.created_at)}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined"
                      color={r.provider === 'ccpayment' ? 'primary' : 'info'}
                      label={GATEWAY[r.provider]} />
                  </TableCell>
                  <TableCell>{r.username}</TableCell>
                  <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {r.currency}{r.chain ? ` · ${r.chain}` : ''}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>
                    <Tooltip title={r.address || ''}><span>{short(r.address)}</span></Tooltip>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{coin(r.amount, r.currency)}</TableCell>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>
                    <Tooltip title={r.tx || ''}><span>{short(r.tx, 12)}</span></Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={r.remark || ''}>{r.remark || '-'}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {r.action_by || '-'}
                    {r.handled_at && <Typography variant="caption" display="block" color="text.secondary">{when(r.handled_at)}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={r.raw_status}>
                      <Chip size="small" color={STATE_COLOR[r.state] || 'default'} label={r.state} />
                    </Tooltip>
                  </TableCell>
                  {canAct && (
                    <TableCell>
                      {r.state === 'pending' && (
                        <Stack direction="row" spacing={1}>
                          <Button size="small" variant="contained" color="success" onClick={() => open(r, 'approve')}>Approve</Button>
                          <Button size="small" variant="outlined" color="error" onClick={() => open(r, 'reject')}>Reject</Button>
                        </Stack>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow><TableCell colSpan={cols.length} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  No automatic withdrawals.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">{total} total · page {page} / {pages}</Typography>
        <IconButton size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft size={16} /></IconButton>
        <IconButton size="small" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}><ChevronRight size={16} /></IconButton>
      </Stack>

      <Dialog open={!!action} onClose={() => !busy && setAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {action?.kind === 'approve' ? 'Send payout' : 'Reject withdrawal'} · {action?.row.username}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {action && coin(action.row.amount, action.row.currency)}
            {action && ` · ${GATEWAY[action.row.provider]}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, wordBreak: 'break-all' }}>
            {action?.row.chain ? `${action.row.chain} · ` : ''}{action?.row.address}
          </Typography>
          <Alert severity={action?.kind === 'approve' ? 'warning' : 'info'} sx={{ mb: 2 }}>
            {action?.kind === 'approve'
              ? 'This sends the crypto immediately and cannot be undone. Check the address and network first.'
              : 'Nothing has been sent — the amount will be refunded to the player.'}
          </Alert>
          {actionErr && <Alert severity="error" sx={{ mb: 2 }}>{actionErr}</Alert>}
          <TextField autoFocus fullWidth size="small" label="Remark (required)" value={remark}
            onChange={(e) => setRemark(e.target.value)} multiline minRows={2} sx={{ mb: 2 }} />
          {needsCode && (
            <TextField fullWidth size="small" label={codeLabel} placeholder="000000"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              inputProps={{ maxLength: 6, inputMode: 'numeric' }}
              helperText={codeHelp} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color={action?.kind === 'approve' ? 'success' : 'error'}
            disabled={busy || !remark.trim() || (needsCode && code.trim().length !== 6)} onClick={submit}>
            {busy ? 'Working…' : action?.kind === 'approve' ? 'Approve & send' : 'Reject & refund'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
